// Copyright (c) 2021, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import PropTypes from 'prop-types'
import { Mutation } from 'react-apollo'
import { withRouter } from 'react-router-dom'
import qs from 'query-string'
import moment from 'moment'

import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { faHandHoldingUsd } from '@fortawesome/free-solid-svg-icons'

import {
  FilterSidebar,
  FilterPicklistWithData,
  FilterWithdrawalProduct,
  FilterDateRange,
  WithdrawalBatches,
  Button,
  WithdrawalBatchAddEditModal as AddModal,
} from '../components'

import { hasDateChanged } from '../utils'

import { MUTATION_WITHDRAWAL_BATCH } from '../mutations'


class WithdrawalBatchList extends React.Component {
  static propTypes = {
    identityId: PropTypes.string
  }

  state = {
    productFilter: '',
    statusFilter: '',
    initiateOnStartDate: null,
    initiateOnEndDate: null,
    orderBy: 'initiate_on',
    currentActiveTab: 0,
    showAddModal: false
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search) {
      this.setFiltersFromQueryString(nextProps.location.search)
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.location.search === '' &&
      (
        nextState.productFilter !== '' ||
        nextState.status !== '' ||
        nextState.initiateOnStartDate ||
        nextState.initiateOnEndDate ||
        nextState.orderBy !== ''
      )
    ) {
      this.setQueryString(nextState)
      return false
    }
    else if (Object.is(this.props.location.search, nextProps.location.search) && (
      this.state.productFilter !== nextState.productFilter ||
      this.state.statusFilter !== nextState.statusFilter ||
      hasDateChanged(this.state.initiateOnStartDate, nextState.initiateOnStartDate) ||
      this.state.orderBy !== nextState.orderBy
    )) {
      this.setQueryString(nextState)
      return false
    }
    else if (this.state.productFilter !== nextState.productFilter ||
      this.state.statusFilter !== nextState.statusFilter ||
      hasDateChanged(this.state.initiateOnStartDate, nextState.initiateOnStartDate) ||
      this.state.orderBy !== nextState.orderBy
    ) {
      this.setQueryString(nextState)
      return false
    }

    return true
  }

  componentDidMount = () => {
    // this life-cycle function implementation ensures that, on first render and/or
    // returning from another page, that filters will still be set to url and what it was before
    if (this.props.location.search) {
      this.setFiltersFromQueryString(this.props.location.search)
    } else {
      this.setQueryString(this.state)
    }
  }

  setFiltersFromQueryString = (queryString) => {
    let queryParams = qs.parse(queryString)

    let filters = Object.assign(
      {},
      ...Object.keys(queryParams).map(key => (  // ES6 way of assigning renamed object keys
        [ 'orderBy' ].indexOf(key) > -1
          ? { [key]: queryParams[key] }
          : [  'initiateOnStartDate', 'initiateOnEndDate' ].indexOf(key) > -1  // end/startDate query params aren't translated into state variables with -'Filter' appended like the others
            ? { [ key ]: moment(queryParams[key], 'YYYYMMDDT') }
            : { [`${key}Filter`]: queryParams[key] }
      ))
    )

    this.setState(filters)
  }

  setQueryString = (state) => {
    let filters = {
      orderBy: state.orderBy
    }

    if (state.productFilter !== '') {
      filters.product = state.productFilter
    }

    if (state.statusFilter !== '') {
      filters.status = state.statusFilter
    }

    if (state.initiateOnStartDate) {
      filters.initiateOnStartDate = state.initiateOnStartDate.format('YYYYMMDD')
    }
    if (state.initiateOnEndDate) {
      filters.initiateOnEndDate = state.initiateOnEndDate.format('YYYYMMDD')
    }

    if (Object.keys(filters).length > 0) {
      this.props.history.push({
        pathname: '/os/products/withdrawalbatches',
        search: qs.stringify(filters)
      })
    }
  }

  selectTab = (tabIndex) => this.setState({ currentActiveTab: tabIndex })

  toggleAddModal = () => (
    this.setState(prevState => (
      { showAddModal: !prevState.showAddModal }
    ))
  )

  saveNewWithdrawalBatch = (withdrawalbatchMutate, productId, initiateOn, initiateWorkflowAt) => {
    let input = {
      product: productId,
      initiateOn,
      initiateWorkflowAt,
      status: 'pending',
      initiatedBy: localStorage.getItem('userUUID')
     }

    withdrawalbatchMutate({ variables: { input } })
    .then(() => {
      this.toggleAddModal()
      this.setState({ listingRefreshTimestamp: moment.unix() })
    })
    .catch(error => console.error(error))
  }

  render() {
    let filters = [
      <FilterWithdrawalProduct
        value={this.state.productFilter}
        onChange={product => (
          this.setState({
            productFilter: product === 'All' ? '' : product
          })
        )}
      />,
      <FilterPicklistWithData
        id='status'
        title='Status'
        forField='WithdrawalStatus'
        default='All'
        symbol='chevrondown'
        value={this.state.statusFilter}
        onChange={status => this.setState({ statusFilter: status })}
        key='filterStatus'
      />,
      <FilterDateRange
        title='Initiate on between'
        startDate={this.state.initiateOnStartDate}
        endDate={this.state.initiateOnEndDate}
        onStartDateChange={date => this.setState({ initiateOnStartDate: date }, () => this.setQueryString(this.state))}
        onEndDateChange={date => this.setState({ initiateOnEndDate: date }, () => this.setQueryString(this.state))}
        startDatePlaceholder='From'
        endDatePlaceholder='To'
        dateFormat='DD MMM YYYY'
        showYearDropdown={true}
        showMonthDropdown={true}
        key='initiateOnBetweenFilter'
      />
    ]

    if (this.props.identityId) {
      filters.splice(1,1)  // removes FilterComboEmployerDivision
    }

    const { productFilter, statusFilter, initiateOnStartDate, initiateOnEndDate, orderBy, showAddModal, listingRefreshTimestamp } = this.state

    return (
      <div className="slds-grow slds-scrollable_y" role="main">
        <div className="slds-grid slds-wrap slds-size_1-of-1">
          <div className="tab-nav slds-size_1-of-1 u-bg-color-white product-listing-page-header">
            <div
              className="u-font-size-x-large u-text-caps slds-p-left_small product-listing-page-header_title"
              style={{ alignItems: "center" }}
            >
              <div
                className="slds-text-color_weak"
                style={{ padding: ".25em .15em .25em .75em" }}
              >
                <Icon icon={faHandHoldingUsd} size="2x" />
                <Icon
                  icon={faHandHoldingUsd}
                  size="1x"
                  style={{ transform: "translate(-0.6em, -0.85em)" }}
                />
              </div>
              <div>Withdrawal batches</div>
            </div>
          </div>
          <FilterSidebar
            actions={
              <Mutation mutation={MUTATION_WITHDRAWAL_BATCH}>
                {(withdrawalbatchMutate) => (
                  <Button onClick={this.toggleAddModal}>
                    Add withdrawal batch
                  </Button>
                )}
              </Mutation>
            }
            filters={filters}
            filtersApplied={
              [
                productFilter,
                statusFilter,
                initiateOnStartDate ? initiateOnStartDate.toString() : "",
                initiateOnEndDate ? initiateOnEndDate.toString() : "",
              ].filter((filter) => filter.length > 0).length
            }
            onClear={() =>
              this.setState({
                productFilter: "",
                statusFilter: "",
                createdAtStartDate: null,
                createdAtEndDate: null,
              })
            }
            componentFilters={true}
          />
          <div className="slds-col slds-size_10-of-12">
            <WithdrawalBatches
              productId={productFilter}
              status={statusFilter === "all" ? "" : statusFilter}
              initiateOnStartDate={
                initiateOnStartDate
                  ? moment.utc(initiateOnStartDate).format("YYYY-MM-DD")
                  : undefined
              }
              initiateOnEndDate={
                initiateOnEndDate
                  ? moment.utc(initiateOnEndDate).format("YYYY-MM-DD")
                  : undefined
              }
              orderBy={orderBy}
              changeSort={(orderBy) => this.setState({ orderBy })}
              key={listingRefreshTimestamp}
            />
          </div>
        </div>
        {showAddModal && (
          <Mutation mutation={MUTATION_WITHDRAWAL_BATCH}>
            {(withdrawalbatchMutate) => (
              <AddModal
                title="Add withdrawal batch"
                onCancelClick={this.toggleAddModal}
                onSaveClick={(productId, initiateOn, initiateWorkflowAt) => {
                  this.saveNewWithdrawalBatch(
                    withdrawalbatchMutate,
                    productId,
                    initiateOn,
                    initiateWorkflowAt
                  );
                }}
              />
            )}
          </Mutation>
        )}
      </div>
    );
  }
}

export default withRouter(WithdrawalBatchList)



// WEBPACK FOOTER //
// ./src/containers/WithdrawalBatchList.js